import type { ItemUI } from '@libs/common-node';

import type { AddToCartBaseItem, AddToCartEcommerce, FulfilmentMethodSelection, YesNo } from '@/shared/analytics';

type MerchandisingHierarchyData = {
    vicePresidentName?: string;
    division?: string;
    department?: string;
    class?: string;
    category?: string;
};

type BuildAddToCartBaseItemParams = {
    product: ItemUI;
    defaultSku: string;
    merchandisingHierarchy: Maybe<MerchandisingHierarchyData>;
    fulfillmentMethod: FulfilmentMethodSelection;
    storeId: string;
    searchTerm: string | null;
    itemListId: string;
    itemListName: string;
    itemCartOrder: number;
};

export function buildAddToCartBaseItem({
    product,
    defaultSku,
    merchandisingHierarchy,
    fulfillmentMethod,
    storeId,
    searchTerm,
    itemListId,
    itemListName,
    itemCartOrder,
}: BuildAddToCartBaseItemParams): AddToCartBaseItem {
    const autoshipAvailability: YesNo = product?.autoshipStockAvailability ? 'yes' : 'no';
    const autoshipFrequency = product?.autoshipFrequency ? String(product.autoshipFrequency) : '';

    return {
        autoship_autoselected: 'no',
        autoship_availability: autoshipAvailability,
        autoship_frequency: autoshipFrequency,
        color: product.variant?.color ?? '',
        default_version: product.sku === defaultSku ? 'yes' : 'no',
        flavor: product?.variant?.flavor ?? '',
        fulfillment_method: fulfillmentMethod,
        item_brand: product?.merchandiseBrand ?? '',
        item_cart_order: itemCartOrder,
        item_id: String(product?.masterProductKey),
        item_list_id: itemListId,
        item_list_name: itemListName,
        item_name: product.name,
        item_search_term: searchTerm,
        item_variant: product.sku,
        price: product.basePrice,
        item_category: merchandisingHierarchy?.vicePresidentName ?? '',
        item_category2: merchandisingHierarchy?.division ?? '',
        item_category3: merchandisingHierarchy?.department ?? '',
        item_category4: merchandisingHierarchy?.class ?? '',
        item_category5: merchandisingHierarchy?.category ?? '',
        quantity: 1,
        size: product?.variant?.size ?? '',
        store_id: storeId,
    };
}

type BuildAddToCartEcommerceDataParams = {
    categories: string[];
    currency: 'USD' | 'CAD';
    value: number;
    productRecommendation: YesNo;
};

export function buildAddToCartEcommerceData({
    categories,
    currency,
    value,
    productRecommendation,
}: BuildAddToCartEcommerceDataParams): Omit<AddToCartEcommerce<AddToCartBaseItem>, 'items'> {
    return {
        add_to_cart_type: 'add',
        category: categories[0] ?? '',
        currency,
        pdp_type: 'standard pdp',
        product_recommendation: productRecommendation,
        sub_category1: categories[1] ?? '',
        sub_category2: categories[2] ?? '',
        value,
    };
}
